// Auth storage utility for storing credentials in browser

const AUTH_STORAGE_KEY = 'wedding_admin_auth';
const AUTH_EXPIRY_HOURS = 24;

export interface AuthCredentials {
  username: string;
  password: string;
  expiresAt: number;
}

/**
 * Store auth credentials in localStorage with expiration
 */
export function storeAuthCredentials(username: string, password: string): void {
  const expiresAt = Date.now() + AUTH_EXPIRY_HOURS * 60 * 60 * 1000;
  const credentials: AuthCredentials = {
    username,
    password,
    expiresAt,
  };
  localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(credentials));
}

/**
 * Get auth credentials from localStorage if not expired
 */
export function getAuthCredentials(): AuthCredentials | null {
  const stored = localStorage.getItem(AUTH_STORAGE_KEY);
  if (!stored) {
    return null;
  }

  try {
    const credentials: AuthCredentials = JSON.parse(stored);
    if (Date.now() > credentials.expiresAt) {
      // Expired, remove it
      clearAuthCredentials();
      return null;
    }
    return credentials;
  } catch (error) {
    console.error('Error parsing stored credentials:', error);
    clearAuthCredentials();
    return null;
  }
}

/**
 * Clear auth credentials from localStorage
 */
export function clearAuthCredentials(): void {
  localStorage.removeItem(AUTH_STORAGE_KEY);
}

/**
 * Create Basic Auth header value
 */
export function createBasicAuthHeader(username: string, password: string): string {
  const credentials = `${username}:${password}`;
  const base64Credentials = btoa(credentials);
  return `Basic ${base64Credentials}`;
}

/**
 * Get stored auth header if credentials exist and are valid
 */
export function getStoredAuthHeader(): string | null {
  const credentials = getAuthCredentials();
  if (!credentials) {
    return null;
  }
  return createBasicAuthHeader(credentials.username, credentials.password);
}
